/**
 * This component contains code to edit an existing machine,
 * serial number and hardware version is non editable.
 */
import React from "react";
import {
  Typography,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Container,
  MenuItem,
  Box,
  IconButton,
  InputLabel
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import { Controller } from "react-hook-form";
import useForm from "hooks/useForm";
import { useTranslation } from "react-i18next";
import "./EditMachine.scss";
import DatePicker from "react-datepicker";
import Settings from "@mui/icons-material/Settings";
import Close from "@mui/icons-material/Close";
import { EditMachineForm } from "./EditMachine.types";
import DashedDivider from "common/ui/DashedDivider";
import QueryStatus from "types/QueryStatus.enum";
import { useClinics, useUpdateMachine } from "services/machines.service";
import yup from "helpers/validator";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import MachineUse from "types/MachineType.enum";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "context/hooks";
import { selectEditMachine } from "context/modal.selector";
import { setEditMachine } from "context/modal.context";
import toast from "react-hot-toast";
import LoadingButton from "common/ui/LoadingButton";

function EditMachine() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const props = useAppSelector(selectEditMachine);

  const editMachineFormSchema = yup.object().shape({
    machineUseId: yup
      .mixed<MachineUse>()
      .oneOf([MachineUse.clinical, MachineUse.nonClinical])
      .required(),
    clinic: yup
      .string()
      .nullable()
      .when("machineUseId", {
        is: MachineUse.nonClinical,
        then: (schema) => schema.transform(() => null)
      })
      .transform((val) => val || null),
    machineName: yup
      .string()
      .matches(/^[a-zA-Z0-9/-\s()_]*$/g, t("editMachine.machineAlphaNum"))
      .max(50, t("editMachine.machineNameLimit", { count: 50 }))
      .when("serial", ([serial], schema) => {
        return schema.transform((val) => val?.trim() || serial);
      }),
    serviceDue: yup.date().nullable()
  });

  React.useEffect(() => {
    if (props.open) {
      const serviceDue = dayjs(props.machine?.serviceDue);
      reset({
        machineName: props.machine?.name,
        hardware: props.machine?.hardwareName,
        serial: props.machine?.serialNumber,
        machineUseId: props.machine?.machineUseId,
        clinic: props.machine?.organisationSiteId,
        serviceDue: serviceDue.isValid() ? serviceDue.toDate() : null
      });
    }
  }, [props.open]);

  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    clearErrors,
    reset
  } = useForm<EditMachineForm>({
    resolver: yupResolver(editMachineFormSchema)
  });

  const machineUseId = watch("machineUseId");
  const clinics = useClinics();

  const updateMachine = useUpdateMachine(onMachineUpdated);

  function onMachineUpdated(_data, error) {
    queryClient.invalidateQueries({ queryKey: ["machine"], exact: false });

    let message = t("editMachine.machineUpdatedSuccessfully");

    if (!error) {
      toast.success(message);
    }

    clearErrors();
    dismiss();
  }
  function onModalDismiss() {
    clearErrors();
    dismiss();
  }
  function dismiss() {
    dispatch(
      setEditMachine({
        open: false,
        machine: null
      })
    );
  }

  function onSubmit(data: EditMachineForm) {
    updateMachine.mutate({
      machineId: props.machine?.id,
      ...data,
      serviceDue: data.serviceDue?.toISOString()
    });
  }

  return (
    <Dialog open={props.open} scroll="body" className="edit-machine">
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" alignItems="center">
            <Settings color="gray" sx={{ marginRight: 1 }} />
            <Typography
              color="gray.main"
              variant="h6"
              fontWeight="medium"
              alignSelf="center"
            >
              {t("editMachine.title")}
            </Typography>
          </Stack>
          <IconButton onClick={onModalDismiss}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack direction="row">
          <Controller
            control={control}
            name="machineUseId"
            render={({ field: { value, onChange } }) => (
              <ToggleButtonGroup
                exclusive
                fullWidth
                onChange={(_, newValue) =>
                  newValue !== null && onChange(newValue)
                }
                value={value}
              >
                <ToggleButton
                  value={MachineUse.clinical}
                  classes={{
                    root: "edit-machine__toggle-button",
                    selected: "edit-machine__toggle-button--active"
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t("editMachine.clinical")}
                  </Typography>
                </ToggleButton>
                <ToggleButton
                  value={MachineUse.nonClinical}
                  classes={{
                    root: "edit-machine__toggle-button",
                    selected: "edit-machine__toggle-button--active"
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t("editMachine.nonClinical")}
                  </Typography>
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          />
        </Stack>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Container maxWidth="xl" sx={{ marginBottom: 2 }}>
            <InputLabel htmlFor="serial" required>
              {t("editMachine.serial")}
            </InputLabel>
            <Controller
              control={control}
              name="serial"
              render={({ field: { value, onChange } }) => (
                <TextField
                  id="serial"
                  fullWidth
                  type="text"
                  value={value}
                  onChange={onChange}
                  disabled
                  InputProps={{
                    readOnly: true
                  }}
                  helperText={errors.serial?.message}
                  error={!!errors.serial}
                  sx={{ backgroundColor: "grey.100" }}
                />
              )}
            />

            <InputLabel htmlFor="hardware" required>
              {t("editMachine.hardware")}
            </InputLabel>
            <TextField
              {...register("hardware")}
              id="hardware"
              fullWidth
              variant="outlined"
              disabled
              InputProps={{
                readOnly: true
              }}
              sx={{ backgroundColor: "grey.100" }}
            />

            {/* hidden clinic field as obsolete, remove false to restore the changes */}
            {false &&
            machineUseId === MachineUse.clinical &&
            clinics.status === QueryStatus.success ? (
              <>
                <InputLabel htmlFor="clinic">
                  {t("editMachine.clinic")}
                </InputLabel>
                <Controller
                  control={control}
                  name="clinic"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      inputProps={{ id: "clinic" }}
                      select
                      fullWidth
                      value={field.value || ""}
                      name="clinic"
                      SelectProps={{
                        displayEmpty: true,
                        MenuProps: { sx: { maxHeight: 300 } }
                      }}
                    >
                      <MenuItem value="">{t("common.na")}</MenuItem>
                      {clinics.data.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </>
            ) : null}

            <InputLabel htmlFor="machineName">
              {t("editMachine.machineName")}
            </InputLabel>
            <TextField
              {...register("machineName")}
              fullWidth
              variant="outlined"
              id="machineName"
              helperText={errors.machineName?.message}
              error={!!errors.machineName}
            />

            <InputLabel>{t("editMachine.serviceDue")}</InputLabel>
            <Controller
              control={control}
              name="serviceDue"
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  onChange={onChange}
                  selected={value}
                  inline
                  disabledKeyboardNavigation
                />
              )}
            />
          </Container>

          <DashedDivider />

          <Box display="flex" justifyContent="center" margin={2}>
            <LoadingButton
              type="submit"
              variant="contained"
              size="large"
              classes={{ root: "edit-machine__save-button" }}
              startIcon={<DoneIcon />}
              loading={updateMachine.isLoading}
            >
              {t("common.save")}
            </LoadingButton>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default EditMachine;
